import type { GeometryElement } from 'jsxgraph';
import type { JsxgraphThemeService } from '../../jsxgraph-theme.service';
import type { ElementCreatorContext } from '../core/board.types';
import { resolveColor } from '../core/color.utils';
import { deepMerge } from '../core/deepMerge';
import { isElement } from '../core/element.utils';
import { createPoint } from '../point/point.create';
import type { PointInput } from '../line/line.model';
import type { CircleStyle, ElementTheme, PointStyle, PolygonStyle } from '../theme/theme.model';
import type { ArcArgs, ArcResult } from './arc.model';

/**
 * Resolve a point input to an existing or newly created GeometryElement.
 */
function resolvePoint(
  ctx: ElementCreatorContext,
  themeService: JsxgraphThemeService,
  theme: ElementTheme,
  input: PointInput,
  name: string,
  style?: PointStyle,
): GeometryElement {
  const { board } = ctx;

  if (isElement(input)) {
    return input;
  }

  if (typeof input === 'string') {
    const found = board.elementsByName[input];
    if (!found) {
      throw new Error(`Point "${input}" not found on board`);
    }
    return found as GeometryElement;
  }

  const result = createPoint(ctx, themeService, theme, {
    coords: input,
    name,
    style,
  });
  return result.point;
}

/**
 * Build JSXGraph arc/sector attributes from style configuration.
 */
function buildArcAttributes(
  themeService: JsxgraphThemeService,
  baseStyle: ElementTheme['circle'],
  styleOverrides?: CircleStyle,
): Record<string, unknown> {
  const style = deepMerge(baseStyle, styleOverrides);
  const strokeColor = resolveColor(themeService, style.strokeColor);
  const fillColor = resolveColor(themeService, style.fillColor);

  return {
    strokeColor,
    fillColor,
    fillOpacity: style.fillOpacity ?? 0,
    strokeWidth: style.strokeWidth,
    dash: style.dash,
    visible: styleOverrides?.visible ?? true,
    withLabel: false,
  };
}

function buildSectorAttributes(
  themeService: JsxgraphThemeService,
  styleOverrides?: PolygonStyle,
): Record<string, unknown> {
  const fillColor = resolveColor(themeService, styleOverrides?.fillColor ?? 'sky');

  return {
    strokeWidth: 0,
    fillColor,
    fillOpacity: styleOverrides?.fillOpacity ?? 0.1,
    visible: styleOverrides?.visible ?? true,
    withLabel: false,
    highlight: false,
  };
}

/**
 * Create an arc element with optional sector fill.
 *
 * Uses JSXGraph native `board.create('arc', ...)` for the arc curve
 * and optionally `board.create('sector', ...)` for the filled sector.
 */
export function createArc(
  ctx: ElementCreatorContext,
  themeService: JsxgraphThemeService,
  theme: ElementTheme,
  args: ArcArgs,
): ArcResult {
  const { board } = ctx;
  const { name: _name = '', style, centerStyle, startPointStyle, endPointStyle, sectorFill, sectorStyle } = args;

  const arcAttrs = buildArcAttributes(themeService, theme.circle, style);

  const defaultPointStyle: PointStyle = { ...theme.point, visible: false };
  const resolvedCenterStyle = deepMerge(defaultPointStyle, centerStyle) as PointStyle;
  const resolvedStartStyle = deepMerge(defaultPointStyle, startPointStyle) as PointStyle;
  const resolvedEndStyle = deepMerge(defaultPointStyle, endPointStyle) as PointStyle;

  const center = resolvePoint(ctx, themeService, theme, args.center, 'center', resolvedCenterStyle);
  const startPt = resolvePoint(ctx, themeService, theme, args.startPoint, 'start', resolvedStartStyle);
  const endPt = resolvePoint(ctx, themeService, theme, args.endPoint, 'end', resolvedEndStyle);

  const arc = board.create('arc', [center, startPt, endPt], arcAttrs) as GeometryElement;

  const result: ArcResult = { arc, center, startPoint: startPt, endPoint: endPt };

  if (sectorFill) {
    const sectorAttrs = buildSectorAttributes(themeService, sectorStyle);
    result.sector = board.create('sector', [center, startPt, endPt], sectorAttrs) as GeometryElement;
  }

  return result;
}
